import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { VirtualRoomResponse } from '../types/api';

interface BugReportData {
    title: string;
    severity: 'Critical' | 'High' | 'Medium' | 'Low';
    roomId: string; // from history selection
    roomUrl: string;
    environment: string;
    testAccount: string;
    qaseUrl: string;
    browser: string;
    device: string;
    steps: string;
    expectedResult: string;
    actualResult: string;
}

const BugReportCreator = () => {
    const { user } = useAuth();
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

    // History room data
    const [roomHistory, setRoomHistory] = useState<VirtualRoomResponse[]>([]);
    const [selectedRoomId, setSelectedRoomId] = useState<string>('');

    const [formData, setFormData] = useState<BugReportData>({
        title: '',
        severity: 'High',
        roomId: '',
        roomUrl: '',
        environment: 'uat',
        testAccount: user?.email || '',
        qaseUrl: '',
        browser: 'Chrome',
        device: 'Mac',
        steps: '',
        expectedResult: '',
        actualResult: ''
    });

    // Load history on mount
    useEffect(() => {
        try {
            const saved = localStorage.getItem('vcaas_room_history');
            if (saved) {
                const history = JSON.parse(saved);
                setRoomHistory(history.slice(0, 10));
            }
        } catch (error) {
            console.error('Failed to load room history:', error);
        }
    }, []);

    // Update form when room selected
    useEffect(() => {
        if (selectedRoomId) {
            const room = roomHistory.find(r => r.id === selectedRoomId);
            if (room) {
                setFormData(prev => ({
                    ...prev,
                    roomId: room.id,
                    roomUrl: room.adminUrl, // prefer admin/dashboard url
                    environment: room.environment || 'uat',
                    testAccount: room.account || prev.testAccount
                }));
            }
        }
    }, [selectedRoomId, roomHistory]);

    const handleCopyAsana = () => {
        const { title, severity, roomUrl, testAccount, qaseUrl, environment, browser, device, steps, expectedResult, actualResult } = formData;

        const markdown = `**Bug 簡述**
[BUG] ${title}

**嚴重程度**
${severity}

**相關連結**
- 虛擬教室: ${roomUrl || 'N/A'}
- 用戶: ${testAccount}
- Qase 案例: ${qaseUrl || 'N/A'}

**測試環境**
- ${environment} / ${browser} / ${device}

**問題描述**

**測試步驟**
${steps}

**預期結果**
${expectedResult}

**實際結果**
${actualResult}`;

        navigator.clipboard.writeText(markdown).then(() => {
            setToast({ message: '已複製 Asana 格式到剪貼簿！', type: 'success' });
        }).catch(() => {
            setToast({ message: '複製失敗', type: 'error' });
        });
    };

    // Auto-hide toast
    useEffect(() => {
        if (toast) {
            const timer = setTimeout(() => setToast(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [toast]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-red-50 via-pink-50 to-orange-50">
            {/* Header */}
            <div className="bg-white/80 backdrop-blur-md shadow-sm border-b border-white/20 sticky top-0 z-10">
                <div className="max-w-7xl mx-auto px-6 py-4">
                    <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-pink-600 rounded-xl flex items-center justify-center shadow-lg">
                            <span className="text-white text-2xl">🐛</span>
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-gray-900">Bug Report Generator</h1>
                            <p className="text-sm text-gray-500">快速建立標準化的 Bug Report</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Toast */}
            {toast && (
                <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 animate-fade-in-down">
                    <div className={`px-6 py-3 rounded-full shadow-lg flex items-center space-x-2 ${toast.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
                        <span>{toast.type === 'success' ? '✅' : '❌'}</span>
                        <span className="font-medium text-sm">{toast.message}</span>
                    </div>
                </div>
            )}

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-6 py-8">
                <div className="bg-white/70 backdrop-blur-md rounded-2xl shadow-xl border border-white/20 overflow-hidden">

                    {/* Section: Classroom Selection */}
                    <div className="p-8 border-b border-gray-100">
                        <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                            <span className="bg-blue-100 text-blue-600 p-2 rounded-lg mr-3">🏫</span>
                            選擇虛擬教室 (自動填入資料)
                        </h2>
                        <select
                            value={selectedRoomId}
                            onChange={(e) => setSelectedRoomId(e.target.value)}
                            className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all font-mono text-sm"
                        >
                            <option value="">-- 手動填寫或選擇歷史教室 --</option>
                            {roomHistory.map(room => (
                                <option key={room.id} value={room.id}>
                                    [{room.environment?.toUpperCase() || 'UNKNOWN'}] {room.name} ({new Date(room.createdAt).toLocaleString()})
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8">

                        {/* Left Column: Form Fields */}
                        <div className="space-y-6">
                            {/* Basic Info */}
                            <div className="bg-white/50 p-6 rounded-xl border border-gray-200">
                                <h3 className="text-sm font-bold text-gray-500 uppercase mb-4 tracking-wider">基本資訊</h3>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Bug 標題</label>
                                        <div className="flex">
                                            <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                                                [BUG]
                                            </span>
                                            <input
                                                type="text"
                                                value={formData.title}
                                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                                                placeholder="簡述問題..."
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">嚴重程度</label>
                                            <select
                                                value={formData.severity}
                                                onChange={(e) => setFormData({ ...formData, severity: e.target.value as any })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                                            >
                                                <option value="Critical">Critical 🔴</option>
                                                <option value="High">High 🟠</option>
                                                <option value="Medium">Medium 🟡</option>
                                                <option value="Low">Low 🟢</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Qase URL / ID</label>
                                            <input
                                                type="text"
                                                value={formData.qaseUrl}
                                                onChange={(e) => setFormData({ ...formData, qaseUrl: e.target.value })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                                placeholder="Optional"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Environment */}
                            <div className="bg-white/50 p-6 rounded-xl border border-gray-200">
                                <h3 className="text-sm font-bold text-gray-500 uppercase mb-4 tracking-wider">測試環境</h3>
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">環境</label>
                                        <select
                                            value={formData.environment}
                                            onChange={(e) => setFormData({ ...formData, environment: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                                        >
                                            <option value="dev">DEV</option>
                                            <option value="staging">STAGING</option>
                                            <option value="uat">UAT</option>
                                            <option value="production">PRODUCTION</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">測試帳號</label>
                                        <input
                                            type="text"
                                            value={formData.testAccount}
                                            onChange={(e) => setFormData({ ...formData, testAccount: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">瀏覽器</label>
                                        <select
                                            value={formData.browser}
                                            onChange={(e) => setFormData({ ...formData, browser: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                        >
                                            <option value="Chrome">Chrome</option>
                                            <option value="Safari">Safari</option>
                                            <option value="Firefox">Firefox</option>
                                            <option value="Edge">Edge</option>
                                            <option value="App">App</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">裝置</label>
                                        <select
                                            value={formData.device}
                                            onChange={(e) => setFormData({ ...formData, device: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                        >
                                            <option value="Mac">Mac</option>
                                            <option value="Windows">Windows</option>
                                            <option value="iOS">iOS</option>
                                            <option value="Android">Android</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="mt-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Room URL / ID</label>
                                    <input
                                        type="text"
                                        value={formData.roomUrl}
                                        onChange={(e) => setFormData({ ...formData, roomUrl: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 font-mono text-xs"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Right Column: Details & Output */}
                        <div className="space-y-6">
                            <div className="bg-white/50 p-6 rounded-xl border border-gray-200 h-full flex flex-col">
                                <h3 className="text-sm font-bold text-gray-500 uppercase mb-4 tracking-wider">問題詳情</h3>

                                <div className="flex-1 space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">測試步驟 (Steps)</label>
                                        <textarea
                                            value={formData.steps}
                                            onChange={(e) => setFormData({ ...formData, steps: e.target.value })}
                                            className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 font-mono text-sm"
                                            placeholder="1. Go to... 2. Click..."
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">預期結果 (Expected)</label>
                                            <textarea
                                                value={formData.expectedResult}
                                                onChange={(e) => setFormData({ ...formData, expectedResult: e.target.value })}
                                                className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500/20 focus:border-green-500 text-sm"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">實際結果 (Actual)</label>
                                            <textarea
                                                value={formData.actualResult}
                                                onChange={(e) => setFormData({ ...formData, actualResult: e.target.value })}
                                                className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500/20 focus:border-red-500 text-sm"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <button
                                    onClick={handleCopyAsana}
                                    className="mt-6 w-full bg-gradient-to-r from-gray-800 to-gray-700 hover:from-black hover:to-gray-800 text-white font-bold py-4 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
                                >
                                    <span>📋</span>
                                    <span>複製為 Asana 格式</span>
                                </button>
                                <p className="text-center text-xs text-gray-500 mt-2">點擊後可直接貼到 Asana Task Description</p>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default BugReportCreator;
